import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import ScrollProgress from "./ScrollProgress";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import MegaMenu from "./nav/MegaMenu";
import MegaUtilityBar from "./nav/MegaUtilityBar";
import { megaNav } from "./nav/megaNavConfig";
import logoHorizontal from "@/assets/logo-horizontal.svg";

interface HeaderV2Props {
  theme?: "dark" | "light";
}

const HeaderV2 = ({ theme = "dark" }: HeaderV2Props) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(href + "/");

  const closeSheet = () => setOpen(false);
  const lightMode = theme === "light";
  const onLight = scrolled || lightMode;

  return (
    <header
      style={{ top: scrolled ? 0 : "var(--banner-h, 0px)" }}
      className={cn(
        "fixed left-0 right-0 z-50 transition-colors duration-300",
        scrolled
          ? "bg-white/95 backdrop-blur-md text-gray-900 shadow-sm"
          : lightMode
            ? "bg-transparent text-foreground"
            : "bg-transparent text-white",
      )}
    >
      <div className="relative flex items-center justify-between h-[72px] px-6 md:px-10">
        <Link
          to="/"
          aria-label="deepc home"
          className="inline-flex items-center text-current"
        >
          <img
            src={logoHorizontal}
            alt="deepc"
            width={120}
            height={24}
            className="h-[20px] md:h-[24px] w-auto"
            style={{ filter: onLight ? "invert(1)" : undefined }}
          />
        </Link>

        <nav className="hidden lg:flex">
          <MegaMenu items={megaNav} dark={!onLight} isActive={isActive} />
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://portal.marketplace.deepc.pro/analytics"
            target="_blank"
            rel="noreferrer"
            className={cn(
              "hidden lg:inline-flex items-center gap-2 rounded-md h-9 px-4 text-[13px] font-medium transition-colors",
              onLight
                ? "text-gray-900 hover:bg-foreground/5 border border-gray-300"
                : "text-white border border-white/40 hover:bg-white/10",
            )}
          >
            Login
          </a>
          <Link
            to="/contact"
            className={cn(
              "hidden sm:inline-flex items-center gap-2 rounded-md h-9 px-4 text-[13px] font-medium transition-colors",
              onLight
                ? "bg-primary text-white hover:bg-[hsl(var(--primary-hover))]"
                : "bg-transparent text-white border border-white/40 hover:bg-white/10",
            )}
          >
            Talk to our team
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              aria-label="Open menu"
              className={cn(
                "lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md text-current transition-colors",
                onLight ? "hover:bg-foreground/5" : "hover:bg-white/10",
              )}
            >
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-seabed text-seabed-foreground border-seabed-line p-0 w-[90%] max-w-sm flex flex-col"
            >
              <div className="flex items-center justify-between px-6 h-16 border-b border-seabed-line/70 shrink-0">
                <img src={logoHorizontal} alt="deepc" className="h-8 w-auto" />
                <SheetClose
                  aria-label="Close menu"
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md text-seabed-foreground/80 hover:bg-seabed-foreground/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-seabed"
                >
                  <X className="h-5 w-5" />
                </SheetClose>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                <Accordion type="single" collapsible className="w-full">
                  {megaNav.map((section) => (
                    <AccordionItem key={section.href} value={section.href} className="border-seabed-line/40">
                      <AccordionTrigger className="text-[16px] font-display text-seabed-foreground hover:no-underline py-4">
                        {section.label}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col gap-1 pb-2">
                          {(() => {
                            const links = section.groups.flatMap((g) => g.links);
                            if (section.label === "Solution") {
                              const order = ["Build", "Deploy", "Govern", "Architecture", "Integrations"];
                              return [...links].sort((a, b) => {
                                const ai = order.indexOf(a.label);
                                const bi = order.indexOf(b.label);
                                return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
                              });
                            }
                            return links;
                          })().map((l) => {
                            const isExternal = /^(https?:|mailto:|tel:)/.test(l.href);
                            const inner = (
                              <>
                                {l.icon && (
                                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                                    <l.icon className="h-3.5 w-3.5" />
                                  </span>
                                )}
                                <span>
                                  <span className="block text-[14px] font-medium text-seabed-foreground">{l.label}</span>
                                  <span className="block text-[12px] text-seabed-foreground/65 leading-snug">
                                    {l.description}
                                  </span>
                                </span>
                              </>
                            );
                            const className = "flex items-start gap-3 rounded-md p-2 hover:bg-seabed-foreground/[0.05]";
                            return isExternal ? (
                              <a key={l.href} href={l.href} onClick={closeSheet} className={className}>
                                {inner}
                              </a>
                            ) : (
                              <Link key={l.href} to={l.href} onClick={closeSheet} className={className}>
                                {inner}
                              </Link>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                <div className="mt-6">
                  <Link
                    to="/contact"
                    onClick={closeSheet}
                    data-theme="dark"
                    className="cta-primary w-full"
                  >
                    Talk to our team
                    <ArrowUpRight className="cta-arrow h-4 w-4" />
                  </Link>
                </div>
              </div>

              <MegaUtilityBar compact onNavigate={closeSheet} />
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <ScrollProgress />
    </header>
  );
};

export default HeaderV2;
